import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Clock, LogIn, Shield, KeyRound, UserCog } from 'lucide-react';
import { formatDateTime, formatRelativeTime } from '@/utils/formatters';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/services/admin-api';

interface UserActivityLogProps {
  userUuid: string;
  lastLoginAt?: string | null;
  lastLoginIp?: string | null;
  failedLoginAttempts?: number;
  lockedUntil?: string | null;
}

export function UserActivityLog({ userUuid, lastLoginAt, lastLoginIp, failedLoginAttempts, lockedUntil }: UserActivityLogProps) {
  // Fetch audit logs related to this user
  const { data: auditData } = useQuery({
    queryKey: ['admin-user-audit', userUuid],
    queryFn: async () => {
      const response = await adminApi.get(`/admin/settings/audit-log?search=${userUuid}&limit=10`);
      return response.data;
    },
    enabled: !!userUuid,
  });

  const auditLogs = auditData?.data || [];

  const actionIcons: Record<string, any> = {
    IMPERSONATE_USER: UserCog,
    RESET_PASSWORD: KeyRound,
    TOGGLE_LOCK: Shield,
    LOGIN: LogIn,
  };

  return (
    <Card>
      <CardHeader title="Journal d'activité" />
      <CardContent>
        <div className="space-y-4">
          {/* Login info */}
          <div className="space-y-2 pb-3 border-b border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center justify-between text-sm">
              <span className="text-neutral-500 flex items-center gap-2">
                <LogIn className="h-3.5 w-3.5" />
                Dernière connexion
              </span>
              <span className="text-neutral-700 dark:text-neutral-300">
                {lastLoginAt ? formatRelativeTime(lastLoginAt) : 'Jamais'}
              </span>
            </div>
            {lastLoginIp && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">IP</span>
                <span className="text-xs font-mono text-neutral-600 dark:text-neutral-400">{lastLoginIp}</span>
              </div>
            )}
            {(failedLoginAttempts ?? 0) > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Tentatives échouées</span>
                <Badge variant="danger">{failedLoginAttempts}</Badge>
              </div>
            )}
            {lockedUntil && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Verrouillé jusqu'au</span>
                <Badge variant="danger">{formatDateTime(lockedUntil)}</Badge>
              </div>
            )}
          </div>

          {/* Audit logs */}
          {auditLogs.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-500 uppercase">Actions admin récentes</p>
              {auditLogs.map((log: any) => {
                const Icon = actionIcons[log.action] || Clock;
                return (
                  <div key={log.uuid || log.id} className="flex items-start gap-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/30 p-2.5">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700">
                      <Icon className="h-3 w-3 text-neutral-600 dark:text-neutral-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-neutral-700 dark:text-neutral-300">{log.action?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-neutral-500">{log.adminEmail} — {formatRelativeTime(log.createdAt)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              message="Aucune action admin"
              description="L'historique des actions admin sur cet utilisateur apparaîtra ici"
              icon={<Clock className="h-8 w-8" />}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
